/**
 * Everything the shop has not finished with, on one screen.
 *
 * WHY THIS EXISTS
 * ---------------
 * A cashier could find out what was still open, but only by knowing which of
 * three places to look: the floor plan for tables, the kitchen board for what
 * was still being cooked, and the orders list for anything else. Nothing put
 * the three together, so the question a cashier actually asks — what is on at
 * the moment, and how long has it been on — had no screen.
 *
 * It sits in front of the menu categories rather than behind a button, because
 * it is the first thing worth looking at when you walk up to the till, and the
 * last thing you want to go hunting for when somebody comes to pay.
 *
 * WHAT A TILE SAYS
 * ----------------
 * Where it is, how long it has been waiting, what it comes to, and where it has
 * got to. The wait is coloured on the same thresholds the kitchen board uses,
 * because a table waiting twenty minutes means the same thing on both screens
 * and two different answers would be worse than none.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { urgency, waited, CALM, WARM, LATE } from '../kitchen/waiting.js';
import { CancelOrderDialog } from './CancelOrderDialog.jsx';

/** Refreshed often: a bill settled at the other till should vanish from this one. */
const POLL_MS = 8_000;

export function useOpenOrders() {
  return useQuery({
    queryKey: ['pos', 'open-orders'],
    queryFn: () => api.get('/pos/orders/open'),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });
}

/** What a pending or decided request was asking for, in a word. */
function kindLabel(action) {
  return { void: 'The cancel', revise: 'The edit', remove_item: 'The change' }[action] ?? 'The change';
}

/** How a tile is labelled. A table has a number; the other two have a kind. */
function where(order) {
  if (order.table_number) return `Table ${order.table_number}`;
  return order.order_type === 'delivery' ? 'Delivery' : 'Takeaway';
}

/**
 * Where the order has got to, in a cashier's words rather than the database's.
 *
 * Deliberately the same wording the floor plan uses, so a cashier and a waiter
 * looking at the same order describe it the same way to a customer.
 */
function progress(order) {
  if (order.served_at) return 'Served';

  return (
    {
      pending: 'Not sent yet',
      confirmed: 'With the kitchen',
      preparing: 'Being cooked',
      ready: 'Ready to serve',
      packed: 'Ready to serve',
      completed: 'Ready to serve',
    }[order.status] ?? order.status
  );
}

const WAIT_TONE = {
  [CALM]: 'text-ink-2',
  [WARM]: 'text-warn',
  [LATE]: 'text-bad',
};

/** Has the kitchen finished with it? */
function kitchenDone(order) {
  return ['ready', 'packed', 'completed'].includes(order.status) || Boolean(order.served_at);
}

/**
 * How worried to be about the time on a tile.
 *
 * WHY NOT SIMPLY THE KITCHEN'S ANSWER
 * -----------------------------------
 * The kitchen board calls twenty minutes late, and it is right to: that is a
 * dish sitting under a lamp. Borrowing it here painted every tile red, because
 * a table is occupied for as long as people are eating and forty minutes at a
 * table is dinner, not a problem. A screen where everything is an emergency
 * says nothing at all.
 *
 * So the clock only counts against an order the kitchen has not finished. Once
 * the food is out, the time is just how long they have been sitting there —
 * worth showing, because it is what a cashier uses to guess who will come to
 * pay next, and worth showing quietly.
 */
function concern(order) {
  return kitchenDone(order) ? CALM : urgency(order.waiting_minutes);
}

export function OpenOrders({ onSettle, onBill, onAddMore, settling }) {
  const open = useOpenOrders();
  const client = useQueryClient();

  const can = useAuth((s) => s.can);
  // Cancelling a placed order is the void endpoint: the cashier already holds
  // the permission, so this is the button that was missing. Only offered on
  // orders not yet paid for — once money has changed hands, putting it right
  // is a refund, not a cancel.
  const canVoid = can('pos.void.pos');
  // Closing out a delivery by hand — for a paid delivery that never went
  // through the rider flow and so has no other way off the board.
  const canComplete = can('pos.create.pos');
  const [cancelling, setCancelling] = useState(null);

  const voidOrder = useMutation({
    mutationFn: ({ id, reason, approval }) =>
      api.post(`/pos/orders/${id}/void`, { reason, ...(approval ? { approval } : {}) }),
    onSuccess: () => {
      // Off the board here, and off the kitchen board too.
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['kitchen'] });
      setCancelling(null);
    },
  });

  const markDelivered = useMutation({
    mutationFn: (id) => api.post(`/pos/orders/${id}/delivered`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['kitchen'] });
    },
  });

  /**
   * The cashier's own approval requests, polled so this board can show a bill as
   * "waiting for a manager" and say when the answer comes back. Only this
   * cashier's, because it is their till the status is for.
   */
  const mine = useQuery({
    queryKey: ['pos', 'approvals', 'mine'],
    queryFn: () => api.get('/pos/approvals/mine'),
    refetchInterval: 5_000,
  });
  const myRequests = mine.data?.requests ?? [];
  const pendingByOrder = new Map(
    myRequests.filter((r) => r.status === 'pending').map((r) => [r.orderId, r])
  );

  // Send a cancel up for approval, instead of taking a manager's PIN here.
  const sendRequest = useMutation({
    mutationFn: ({ orderId, action, payload }) =>
      api.post('/pos/approvals', { action, orderId, payload }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['pos', 'approvals', 'mine'] });
      setCancelling(null);
    },
    onError: (e) => {
      client.invalidateQueries({ queryKey: ['pos', 'approvals', 'mine'] });
      setCancelling(null);
      // A request already waiting is not a problem — the board already shows it.
      if (e?.code !== 'ALREADY_PENDING') {
        setDecision({ tone: 'bad', text: e?.message ?? 'Could not send the request.' });
      }
    },
  });

  /**
   * Saying when a request the cashier sent has been decided.
   *
   * The board already shows a "waiting" bill; when the manager answers, the
   * cashier is three rooms away and will not be watching this poll land. So the
   * decision is said out loud — approved and the bill has changed, or declined
   * with the reason — and the boards are refreshed to catch up.
   */
  const seenDecided = useRef(new Set());
  const firstLook = useRef(true);
  const [decision, setDecision] = useState(null);
  useEffect(() => {
    const decided = myRequests.filter((r) => r.status !== 'pending');
    if (firstLook.current) {
      for (const r of decided) seenDecided.current.add(r.id);
      firstLook.current = false;
      return;
    }
    const fresh = decided.filter((r) => !seenDecided.current.has(r.id));
    for (const r of fresh) seenDecided.current.add(r.id);
    if (fresh.length === 0) return;

    const r = fresh[fresh.length - 1];
    setDecision(
      r.status === 'approved'
        ? { tone: 'good', text: `${kindLabel(r.action)} for ${r.orderNumber} was approved.` }
        : {
            tone: 'bad',
            text: `${kindLabel(r.action)} for ${r.orderNumber} was declined${
              r.decisionNote ? ` — ${r.decisionNote}` : ''
            }.`,
          }
    );
    client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
    client.invalidateQueries({ queryKey: ['kitchen'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine.dataUpdatedAt]);

  if (open.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner label="Looking at what is on" />
      </div>
    );
  }

  const orders = open.data?.orders ?? [];
  const summary = open.data?.summary ?? { dineIn: 0, takeaway: 0, delivery: 0, owed: 0 };

  if (orders.length === 0) {
    return (
      <p className="py-12 text-center text-ink-2 text-sm">
        Nothing is on at the moment. Every bill is settled and the kitchen is clear.
      </p>
    );
  }

  return (
    <div>
      {/* The answer to a request the cashier sent up, said once it lands. */}
      {decision && (
        <div
          role="status"
          className={`mb-3 flex items-center justify-between gap-3 rounded-lg px-3.5 py-2.5 text-sm ${
            decision.tone === 'good' ? 'bg-good/10 text-good' : 'bg-bad-bg text-bad'
          }`}
        >
          <span>{decision.text}</span>
          <button onClick={() => setDecision(null)} className="shrink-0 text-xs underline">
            Dismiss
          </button>
        </div>
      )}

      {/*
        The counts first, because "how busy are we" is answered by a number and
        not by counting tiles.
      */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-rule bg-surface px-3.5 py-2.5">
        <Count n={summary.dineIn} one="table" many="tables" />
        <Count n={summary.takeaway} one="takeaway" many="takeaways" />
        <Count n={summary.delivery} one="delivery" many="deliveries" />

        {summary.owed > 0 && (
          <span className="ml-auto text-sm">
            <span className="text-ink-3">Still owed </span>
            <span className="font-semibold tabular-nums text-ink">{formatPKR(summary.owed)}</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
        {orders.map((order) => {
          const owing = order.payment_status !== 'paid';
          const level = concern(order);
          // A change to this bill is up for approval; until it is decided the
          // bill is left alone and the buttons that would change it stand down.
          const awaiting = pendingByOrder.get(order.id);
          // A paid delivery can be closed out by hand — for the one that never
          // went through a rider and so had no other way off the board.
          const showDelivered = !owing && order.order_type === 'delivery' && canComplete;

          return (
            <div
              key={order.id}
              className={`flex flex-col rounded-xl border bg-surface p-3 ${
                level === LATE ? 'border-bad' : 'border-rule'
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold text-ink">{where(order)}</span>
                <span
                  className={`text-sm tabular-nums ${level === LATE ? 'text-bad' : WAIT_TONE[level]}`}
                >
                  {waited(order.waiting_minutes)}
                </span>
              </div>

              <p className="mt-0.5 truncate text-ink-3 text-xs">{order.order_number}</p>
              <p className="mt-1.5 text-ink-2 text-sm">{progress(order)}</p>

              <p className="mt-2 font-semibold tabular-nums text-ink text-lg">
                {formatPKR(order.total_amount)}
              </p>

              {/*
                Said only when it is true. A paid takeaway still being cooked is
                on this board too, and marking it "owed" would have a cashier
                asking for money that has already been taken.
              */}
              <p className={`text-xs ${owing ? 'text-warn' : 'text-good'}`}>
                {owing ? 'Not paid' : 'Paid'}
              </p>

              {awaiting && (
                <p className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-warn-bg px-2.5 py-1 text-xs font-medium text-warn">
                  Waiting for a manager
                </p>
              )}

              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {owing && !awaiting && (
                  <button
                    onClick={() => onSettle(order)}
                    disabled={settling === order.id}
                    className="flex-1 rounded-lg bg-brand-400 px-2 py-2 font-medium text-surface-2 text-sm disabled:opacity-50"
                  >
                    {settling === order.id ? 'Taking…' : 'Take payment'}
                  </button>
                )}
                {/* Another round — the dishes go onto this bill, not a second
                    one. Only while it is unpaid; a paid bill starts a new order. */}
                {/* Edit the whole order — add or change lines, quantities, the
                    type, the table — before it is paid. Loads it back into the
                    cart; the kitchen sees only what actually changes. */}
                {owing && onAddMore && !awaiting && (
                  <button
                    onClick={() => onAddMore(order)}
                    className="flex-1 rounded-lg border border-brand-400/40 px-2 py-2 text-sm font-medium text-brand-300 hover:bg-brand-400/10"
                  >
                    Add / edit
                  </button>
                )}
                {showDelivered && (
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          'Mark this delivery as delivered? It will be completed and leave the board.'
                        )
                      ) {
                        markDelivered.mutate(order.id);
                      }
                    }}
                    disabled={markDelivered.isPending}
                    className="flex-1 rounded-lg border border-rule px-2 py-2 text-ink-2 text-sm hover:border-good hover:text-good disabled:opacity-50"
                  >
                    Delivered
                  </button>
                )}
                <button
                  onClick={() => onBill(order)}
                  className={`rounded-lg border border-rule px-2.5 py-2 text-ink-2 text-sm hover:bg-surface-3 ${
                    owing || showDelivered ? '' : 'flex-1'
                  }`}
                >
                  Bill
                </button>
                {/* Only while it is unpaid — a paid order put right is a refund,
                    which is its own thing, not a cancel. */}
                {owing && canVoid && !awaiting && (
                  <button
                    onClick={() => setCancelling(order)}
                    className="rounded-lg border border-rule px-2.5 py-2 text-ink-3 text-sm hover:border-bad hover:text-bad"
                    title="Cancel this order"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {cancelling && (
        <CancelOrderDialog
          order={cancelling}
          busy={voidOrder.isPending}
          error={voidOrder.error}
          sending={sendRequest.isPending}
          onConfirm={(reason, approval) =>
            voidOrder.mutate({ id: cancelling.id, reason, approval })
          }
          onSendRequest={(reason) =>
            sendRequest.mutate({ orderId: cancelling.id, action: 'void', payload: { reason } })
          }
          onClose={() => {
            voidOrder.reset();
            setCancelling(null);
          }}
        />
      )}

    </div>
  );
}

/** Left out entirely when it is zero, rather than shown as "0 deliveries". */
function Count({ n, one, many }) {
  if (!n) return null;

  return (
    <span className="text-sm">
      <span className="font-semibold tabular-nums text-ink">{n}</span>{' '}
      <span className="text-ink-2">{n === 1 ? one : many}</span>
    </span>
  );
}
